// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import { useGetApi } from "../../../data/hooks/api";
import { RESOURCE_SERVER_URLS } from "../../../data/configs/spl-config";
import {
   Tabs, Tab, Typography, TableRow, Table, TableCell,
   TableBody, Button, Tooltip, Stack, Box, Paper,
} from "@mui/material";
import { AccountDetails } from "../../../data/utils/types";
import ListAccountProjects from "./ListAccountProjects";
import ListAccountEscalations from "./ListAccountEscalations";
import ListAccountSolutionDocument from "./ListAccountSolutionDocument";
import EscalateDialog from "../../Escalate";
import LinearLoading from "../../LinearLoading";
import { PermissionContext } from '../../Authorize';
import { useContext } from "react";
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import { Summarize } from "@mui/icons-material";
import { ItemNotFoundComponent } from "../../../pages/ItemNotFound";
import { ErrorComponent } from "../../../pages/ErrorPage";
import { Link } from "react-router-dom";
import TeamMembersDrawer from "../../TeamMembersDrawer";
import PathView from "../../PathView";
import styled from "@emotion/styled";
import { Store } from "react-notifications-component";

const folderViewBaseUrl = window.config.googleDriveConfig.folderViewUrl;
const backendUrl = window.config.asgardeoConfig.resourceServerURLs[0];

interface TabPanelProps {
   children: React.ReactNode;
   value: number;
   index: number;
}

interface ListAccountDetailProps {
   id: string;
}

interface AccountHeadingProps {
   data: AccountDetails;
   showViewOnDriveButton: boolean;
}

export default function ListAccountDetail({ id }: ListAccountDetailProps): JSX.Element {
   const [value, setTabValue] = React.useState(0);
   const apiUrl = RESOURCE_SERVER_URLS[0] + `/accounts/${id}`;

   const { data, loading, error, getApiData } = useGetApi<AccountDetails>({
      url: apiUrl,
      headers: { accept: "application/json" },
   });

   React.useEffect(() => {
      getApiData();
   }, []);

   const handleTabChange = (
      event: React.SyntheticEvent,
      newValue: number
   ) => {
      setTabValue(newValue);
   };

   return (
      <>
         {loading
            ? <LinearLoading />
            : error
               ? error.status == 404
                  ? <ItemNotFoundComponent />
                  : <ErrorComponent />
               : data && (
                  <>
                     <PathView accountName={data?.name} accountNumber={data?.number} />
                     <AccountHeading data={data} showViewOnDriveButton={value === 3} /> 
                     <Tabs
                        value={value}
                        onChange={handleTabChange}
                        aria-label="Account Tabs"
                     >
                        <Tab label="Basic Info" />
                        <Tab label="Projects" />
                        <Tab label="Escalations" />
                        <Tab label="Artifacts Repository" /> 
                     </Tabs>
                     <Paper>
                        <TabPanel value={value} index={0}>
                           <Typography component="div">
                              <BasicInfoTable data={data} />
                           </Typography>
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                           <Typography component="div">
                              <ListAccountProjects id={id} />
                           </Typography>
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                           <Typography component="div">
                              <ListAccountEscalations id={id} />
                           </Typography>
                        </TabPanel>
                        <TabPanel value={value} index={3}>
                           <Typography component="div">
                              <ListAccountSolutionDocument driveLocation={data?.driveLocation || ''} />
                           </Typography>
                        </TabPanel>
                     </Paper>
                  </>
               )
         }
      </>
   );
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
   return (
      <div
         role="tabpanel"
         hidden={value !== index}
         id={`simple-tabpanel-${index}`}
         aria-labelledby={`simple-tab-${index}`}
      >
         {value === index && (
            <Typography component="div" sx={{ p: 3 }}>
               {children}
            </Typography>
         )}
      </div>
   );
};

const AccountHeading: React.FC<AccountHeadingProps> = ({ data, showViewOnDriveButton }) => {
   const name = data && data.name ? data.name : "Loading...";
   const number = data && data.number ? data.number : " Account ";
   const [openEscalate, setopenEscalate] = React.useState(false);
   const authInfo = useContext(PermissionContext);

   const handleEscalate = () => {
      setopenEscalate(true);
   };

   // Open folder in Google Drive
   const goToGoogleDrive = () => {
      if (data?.driveLocation) {
         window.open(data.driveLocation, '_blank'); // Open in new tab
      } else {
         console.error('Drive location is not available'); // Log if drive location is missing
      }
   };

   return (
      <>
         <EscalateDialog isEscalateDialogOpen={openEscalate} data={data}
            setIsEscalateDialogOpen={
               function (isDialogOpen: boolean): void {
                  setopenEscalate(isDialogOpen);
               }
            }
         />
         <Stack spacing={0} >
            <Box flex={2} display="flex" justifyContent="center" >
               <Typography variant="h4" gutterBottom fontWeight={'bold'}>
                  {name + "   ( " + number + " )"}
               </Typography>
            </Box>
            <Box flex={2} display="flex" justifyContent="flex-end" marginRight={"5%"} >
               {authInfo.isUserAllowedtoAddEscalations ?
                (
                  <>
                     <Button variant="contained"
                        sx={{
                           color: 'white',
                           marginBottom: '-10px',
                           marginRight: '10px',
                           ':hover': {
                              bgcolor: '#e96900',
                              borderColor: 'primary.main', // theme.palette.primary.main
                              color: 'white',
                           }
                        }}
                        onClick={handleEscalate} startIcon={<EscalatorWarningIcon />}
                     >
                        Escalate
                     </Button>
                     {showViewOnDriveButton && (
                        <Button
                           variant="contained"
                           sx={{
                              color: 'white',
                              marginBottom: '-10px',
                              marginRight: '10px',
                              ':hover': {
                                 bgcolor: '#e96900',
                                 borderColor: 'primary.main',
                                 color: 'white',
                              }
                           }}
                           onClick={goToGoogleDrive}
                           startIcon={<Summarize />}
                        >
                           View on Drive
                        </Button>
                     )}
                  </>
               ) : (
                  <>
                     <Tooltip title="You don't have the permission">
                        <span>
                        <Button variant="contained" disabled startIcon={<EscalatorWarningIcon />}>
                              Escalate
                           </Button>
                        </span>
                     </Tooltip>
                     {showViewOnDriveButton && (
                        <Tooltip title="Go to Google Drive">
                           <Button
                              variant="contained"
                              sx={{
                                 color: 'white',
                                 marginBottom: '-10px',
                                 marginRight: '10px',
                                 ':hover': {
                                    bgcolor: '#e96900',
                                    borderColor: 'primary.main',
                                    color: 'white',
                                 }
                              }}
                              onClick={goToGoogleDrive}
                              startIcon={<EscalatorWarningIcon />}
                           >
                              View on Drive
                           </Button>
                        </Tooltip>
                     )}
                  </>
               )}
            </Box>
         </Stack>
      </>
   );
};

const BasicInfoTable = ({ data }: { data: AccountDetails }) => {
   const rating = (data && data.rating) || "N/A";
   const accountManager = (data && data.accountManager) || "N/A";
   const technicalOwner = (data && data.technicalOwner) || "N/A";
   const customerSuccessManager = (data && data.customerSuccessManager) || "N/A";
   const integrationCSTeamName = (data && data.integrationCSTeamName) || "N/A";
   const integrationCSTeamEmail = (data && data.integrationCSTeamEmail) || "N/A";
   const integrationCSTeamManagerName = (data && data.integrationCSTeamManagerName) || "N/A";
   const integrationCSTeamManagerEmail = (data && data.integrationCSTeamManagerEmail) || "N/A";
   const integrationCSTeamSysId = (data && data.integrationCSTeamSysId) || "";
   const teamScheduleUrl = (data && data.integrationCSTeamScheduleUrl) || "";
   const region = (data && data.region) || "N/A";
   const country = (data && data.country) || "N/A";
   const city = (data && data.city) || "N/A";
   const arr = (data && data.arr) || "N/A";

   const [isDrawerOpen, setIsDrawerOpen] = React.useState(false); // Add state for Drawer

   const handleViewTeamClick = () => {
      setIsDrawerOpen(true);
   };

   const handleTeamScheduleClick = () => {
      if (integrationCSTeamSysId !== "") {
         window.open(`/support/team-schedule/${integrationCSTeamSysId}`, '_blank');
      } else {
         Store.addNotification({
            title: "Error!",
            message: "CRT Team was not found.",
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true,
            },
         });
      }
   }

   return (
      <Box className="table-container" style={{ display: "flex" }}>
         <Box style={{ flex: 1, marginRight: "10rem" }}>
            <Table className="table" style={{ width: "100%" }}>
               <TableBody>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        State:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{rating}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        Region:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{region}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        Country:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{country}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        City:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{city}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        ARR:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{arr}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        Account Manager:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{accountManager.replace(' ⓦ', '')}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        Technical Owner:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{technicalOwner.replace(' ⓦ', '')}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        Customer Success Manager:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{customerSuccessManager}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        CRE Team:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>
                        {integrationCSTeamName !== "N/A" ? (
                           <>
                              <Button
                                 size="small"
                                 sx={{
                                    color: '#e96900',
                                    marginLeft: '-7px',
                                    ':hover': {
                                       bgcolor: '#e96900',
                                       borderColor: 'primary.main',
                                       color: 'white',
                                    }
                                 }}
                                 onClick={handleViewTeamClick}
                              >
                                 {integrationCSTeamName}
                              </Button>
                              <span className="m-2">(<span style={{ margin: "2px" }}>{integrationCSTeamEmail} </span>)</span>
                           </>
                        ) : "N/A"}

                     </TableCell>
                  </TableRow>
                  {integrationCSTeamName !== "N/A" ? (
                     <TableRow className="row-style">
                        <TableCell variant="head" style={{ border: "none" }}> </TableCell>
                        <TableCell style={{ border: "none" }}>
                           <Button
                              size="small"
                              onClick={handleTeamScheduleClick}
                              sx={{
                                 color: '#e96900',
                                 marginLeft: '-5px',
                                 ':hover': {
                                    bgcolor: '#e96900',
                                    borderColor: 'primary.main',
                                    color: 'white',
                                 }
                              }}
                           >
                              Team Schedule
                           </Button>
                        </TableCell>
                     </TableRow>
                  ) : null}
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        CRE Team Lead:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>
                        {integrationCSTeamManagerName.replace(' ⓦ', '')}
                        <span className="m-2">
                           (<span style={{ margin: "2px" }}> {integrationCSTeamManagerEmail}</span>)
                        </span>
                     </TableCell>
                  </TableRow>
               </TableBody>
            </Table>
            <TeamMembersDrawer
               open={isDrawerOpen}
               onClose={() => setIsDrawerOpen(false)}
               teamName={data.integrationCSTeamName}
               teamId={integrationCSTeamSysId}
            />
         </Box>
      </Box>
   );
};
